import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Wallet, Plus, ExternalLink, Trash2 } from "lucide-react";
import { fiscalYearFor } from "@costadorada/shared";
import type { SummaryBreakdownItem } from "@costadorada/shared";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { FileUpload } from "@/components/FileUpload";
import { GoogleDrivePicker } from "@/components/GoogleDrivePicker";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";

interface MonthlySummary {
  id: string;
  periodYear: number;
  periodMonth: number;
  totalIncome: string;
  totalExpenses: string;
  netIncome: string;
  reserveFund?: string | null;
  breakdown?: SummaryBreakdownItem[] | null;
  notes?: string | null;
  publishedAt?: string | null;
  report?: { id: string; fileUrl: string } | null;
}

function monthName(month: number, lang: string): string {
  return new Intl.DateTimeFormat(lang.startsWith("es") ? "es-PR" : "en-US", {
    month: "long",
  }).format(new Date(2000, month - 1, 1));
}

// Default to the month being closed: the previous one.
function previousPeriod(): { year: number; month: number } {
  const now = new Date();
  const month = now.getMonth() === 0 ? 12 : now.getMonth();
  const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  return { year, month };
}

export function MonthlyClosePage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const prev = previousPeriod();

  const [periodYear, setPeriodYear] = useState(prev.year);
  const [periodMonth, setPeriodMonth] = useState(prev.month);
  const [totalIncome, setTotalIncome] = useState("");
  const [totalExpenses, setTotalExpenses] = useState("");
  const [reserveFund, setReserveFund] = useState("");
  const [notes, setNotes] = useState("");
  const [reportFileUrl, setReportFileUrl] = useState("");
  const [breakdown, setBreakdown] = useState<SummaryBreakdownItem[]>([]);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data: summaries = [], isLoading, isError } = useQuery({
    queryKey: ["financial-summaries"],
    queryFn: async () => (await api.get<MonthlySummary[]>("/financial-summaries")).data,
  });

  const existing = useMemo(
    () => summaries.find((s) => s.periodYear === periodYear && s.periodMonth === periodMonth),
    [summaries, periodYear, periodMonth]
  );

  // Edit-in-place: when the selected period already has a summary, load it into the form
  useEffect(() => {
    if (existing) {
      setTotalIncome(existing.totalIncome);
      setTotalExpenses(existing.totalExpenses);
      setReserveFund(existing.reserveFund ?? "");
      setNotes(existing.notes ?? "");
      setReportFileUrl(existing.report?.fileUrl ?? "");
      setBreakdown(existing.breakdown ?? []);
      setShowBreakdown((existing.breakdown ?? []).length > 0);
    } else {
      setTotalIncome("");
      setTotalExpenses("");
      setReserveFund("");
      setNotes("");
      setReportFileUrl("");
      setBreakdown([]);
      setShowBreakdown(false);
    }
    setFeedback(null);
  }, [existing?.id, periodYear, periodMonth]);

  const netIncome =
    totalIncome !== "" && totalExpenses !== ""
      ? parseFloat(totalIncome) - parseFloat(totalExpenses)
      : null;

  const saveMutation = useMutation({
    mutationFn: async (publish: boolean) =>
      (
        await api.post("/financial-summaries", {
          periodYear,
          periodMonth,
          totalIncome: parseFloat(totalIncome),
          totalExpenses: parseFloat(totalExpenses),
          reserveFund: reserveFund === "" ? null : parseFloat(reserveFund),
          breakdown: breakdown.filter((b) => b.label.trim() !== ""),
          notes: notes.trim() === "" ? null : notes.trim(),
          reportFileUrl: reportFileUrl || null,
          publish,
        })
      ).data,
    onSuccess: (_data, publish) => {
      qc.invalidateQueries({ queryKey: ["financial-summaries"] });
      qc.invalidateQueries({ queryKey: ["financial-reports"] });
      setFeedback(publish ? t("finance.close.publishedOk") : t("finance.close.draftOk"));
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/financial-summaries/${id}/unpublish`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["financial-summaries"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/financial-summaries/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["financial-summaries"] }),
    onError: (e: any) => alert(e?.response?.data?.message ?? "Error"),
  });

  const figuresReady = totalIncome !== "" && totalExpenses !== "";
  const canPublish = figuresReady && (reportFileUrl !== "" || Boolean(existing?.report?.fileUrl));

  function handlePublish() {
    if (!window.confirm(t("finance.close.confirmPublish"))) return;
    saveMutation.mutate(true);
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-2xl font-bold flex items-center gap-2">
        <Wallet size={24} /> {t("finance.close.title")}
      </h1>
      <p className="text-sm text-slate-600">{t("finance.close.intro")}</p>

      <div className="card space-y-4">
        {/* 1 — Period */}
        <div>
          <label className="label">{t("finance.close.period")}</label>
          <div className="flex gap-2 items-center">
            <select
              className="input w-44"
              value={periodMonth}
              onChange={(e) => setPeriodMonth(parseInt(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {monthName(m, i18n.language)}
                </option>
              ))}
            </select>
            <input
              type="number"
              className="input w-28"
              min={2000}
              max={2100}
              value={periodYear}
              onChange={(e) => setPeriodYear(parseInt(e.target.value))}
            />
            <span className="text-xs text-slate-500">
              {t("finance.close.fiscalYear")}: {fiscalYearFor(periodYear, periodMonth)}
            </span>
            {existing && (
              <Badge variant={existing.publishedAt ? "success" : "warning"}>
                {existing.publishedAt ? t("finance.close.published") : t("finance.close.draft")}
              </Badge>
            )}
          </div>
        </div>

        {/* 2 — P&L PDF */}
        <div>
          <label className="label">{t("finance.close.uploadPnl")}</label>
          <div className="flex flex-wrap items-center gap-2">
            <FileUpload
              folder="costadorada1/finance"
              accept=".pdf"
              value={reportFileUrl || null}
              onUploaded={setReportFileUrl}
            />
            <GoogleDrivePicker folder="costadorada1/finance" onUploaded={setReportFileUrl} />
          </div>
        </div>

        {/* 3 — Key figures */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="label">{t("finance.close.totalIncome")}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="input"
              placeholder="0.00"
              value={totalIncome}
              onChange={(e) => setTotalIncome(e.target.value)}
            />
          </div>
          <div>
            <label className="label">{t("finance.close.totalExpenses")}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="input"
              placeholder="0.00"
              value={totalExpenses}
              onChange={(e) => setTotalExpenses(e.target.value)}
            />
          </div>
          <div>
            <label className="label">{t("finance.close.reserveFund")}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="input"
              placeholder={t("common.optional")}
              value={reserveFund}
              onChange={(e) => setReserveFund(e.target.value)}
            />
          </div>
        </div>
        {netIncome !== null && (
          <div className="text-sm">
            {t("finance.close.netIncome")}:{" "}
            <span className={`font-bold ${netIncome >= 0 ? "text-green-600" : "text-red-600"}`}>
              {formatCurrency(netIncome)}
            </span>
          </div>
        )}

        {/* 4 — Optional breakdown */}
        <div>
          <button
            type="button"
            className="text-sm text-primary hover:underline"
            onClick={() => setShowBreakdown(!showBreakdown)}
          >
            {showBreakdown ? "− " : "+ "}
            {t("finance.close.breakdown")}
          </button>
          {showBreakdown && (
            <div className="mt-2 space-y-2">
              {breakdown.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input
                    className="input flex-1"
                    placeholder={t("finance.close.lineLabel")}
                    value={item.label}
                    onChange={(e) =>
                      setBreakdown(breakdown.map((b, i) => (i === idx ? { ...b, label: e.target.value } : b)))
                    }
                  />
                  <input
                    type="number"
                    step="0.01"
                    className="input w-32"
                    placeholder="0.00"
                    value={item.amount || ""}
                    onChange={(e) =>
                      setBreakdown(
                        breakdown.map((b, i) =>
                          i === idx ? { ...b, amount: parseFloat(e.target.value) || 0 } : b
                        )
                      )
                    }
                  />
                  <select
                    className="input w-32"
                    value={item.kind}
                    onChange={(e) =>
                      setBreakdown(
                        breakdown.map((b, i) =>
                          i === idx ? { ...b, kind: e.target.value as "INCOME" | "EXPENSE" } : b
                        )
                      )
                    }
                  >
                    <option value="INCOME">{t("finance.close.income")}</option>
                    <option value="EXPENSE">{t("finance.close.expense")}</option>
                  </select>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setBreakdown(breakdown.filter((_, i) => i !== idx))}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {breakdown.length < 20 && (
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  onClick={() => setBreakdown([...breakdown, { label: "", amount: 0, kind: "INCOME" }])}
                >
                  <Plus size={14} /> {t("finance.close.addLine")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* 5 — Optional notes */}
        <div>
          <label className="label">
            {t("finance.close.notes")} ({t("common.optional")})
          </label>
          <textarea
            className="input"
            rows={2}
            maxLength={2000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          {feedback && <span className="text-sm text-green-700 mr-auto">{feedback}</span>}
          {saveMutation.isError && (
            <span className="text-sm text-red-600 mr-auto">
              {(saveMutation.error as any)?.response?.data?.message ?? t("common.error")}
            </span>
          )}
          <button
            className="btn-secondary"
            disabled={!figuresReady || saveMutation.isPending}
            onClick={() => saveMutation.mutate(false)}
          >
            {t("finance.close.saveDraft")}
          </button>
          <button
            className="btn-primary"
            disabled={!canPublish || saveMutation.isPending}
            title={!canPublish ? t("finance.close.pdfRequired") : undefined}
            onClick={handlePublish}
          >
            {t("finance.close.publish")}
          </button>
        </div>
      </div>

      {/* History */}
      <div className="card p-0 overflow-x-auto">
        {isError ? (
          <div className="p-6"><QueryError /></div>
        ) : isLoading ? (
          <div className="p-6 text-slate-500">{t("common.loading")}</div>
        ) : summaries.length === 0 ? (
          <div className="p-6 text-slate-500">—</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">{t("finance.close.period")}</th>
                <th className="text-right px-4 py-2">{t("finance.close.netIncome")}</th>
                <th className="text-left px-4 py-2">{t("finance.status")}</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {summaries.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 capitalize">
                    {monthName(s.periodMonth, i18n.language)} {s.periodYear}
                  </td>
                  <td
                    className={`px-4 py-2 text-right font-medium ${
                      parseFloat(s.netIncome) >= 0 ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {formatCurrency(s.netIncome)}
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant={s.publishedAt ? "success" : "warning"}>
                      {s.publishedAt ? t("finance.close.published") : t("finance.close.draft")}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                    <button
                      className="text-primary hover:underline"
                      onClick={() => {
                        setPeriodYear(s.periodYear);
                        setPeriodMonth(s.periodMonth);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      {t("common.edit")}
                    </button>
                    {s.publishedAt && (
                      <button
                        className="text-amber-700 hover:underline"
                        disabled={unpublishMutation.isPending}
                        onClick={() => unpublishMutation.mutate(s.id)}
                      >
                        {t("finance.close.unpublish")}
                      </button>
                    )}
                    {s.report?.fileUrl && (
                      <a
                        href={s.report.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        <ExternalLink size={12} /> PDF
                      </a>
                    )}
                    <AdminRecordActions
                      compact
                      entityLabel={`${monthName(s.periodMonth, i18n.language)} ${s.periodYear}`}
                      onDelete={() => deleteMutation.mutate(s.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
